import { useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

interface CookiePolicyModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export default function CookiePolicyModal({ isOpen, onClose }: CookiePolicyModalProps) {
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
          className="fixed inset-0 z-[60] flex items-center justify-center px-4"
          onClick={onClose}
        >
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" />

          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            transition={{ duration: 0.25 }}
            className="relative bg-white rounded-2xl w-full max-w-2xl max-h-[85vh] flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="flex items-center justify-between px-8 py-5 border-b border-gray-100 flex-shrink-0">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 flex items-center justify-center">
                  <i className="ri-shield-check-line text-2xl text-primary"></i>
                </div>
                <h2 className="font-serif text-xl font-bold text-dark">Cookie Policy</h2>
              </div>
              <button
                onClick={onClose}
                className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-gray-100 transition-colors cursor-pointer"
                aria-label="Chiudi"
              >
                <i className="ri-close-line text-xl text-gray-500"></i>
              </button>
            </div>

            {/* Scrollable Content */}
            <div className="overflow-y-auto px-8 py-6 flex-1 text-sm text-gray-700 leading-relaxed space-y-5">
              <p className="text-xs text-gray-500 italic">
                Ultimo aggiornamento: aprile 2025
              </p>

              <p>
                Il presente sito web di <strong>Big Finance S.r.l.</strong> (P.IVA IT-06146231003), con sede in Via Antonio Chinotto 1, 00195 Roma (RM), Italia, utilizza cookie e tecnologie simili per garantire il corretto funzionamento del sito, migliorare l&apos;esperienza di navigazione e, previo consenso, per finalità analitiche e di marketing.
              </p>

              <div>
                <h3 className="font-bold text-dark mb-2">Cosa sono i cookie?</h3>
                <p>
                  I cookie sono piccoli file di testo che i siti web visitati dall&apos;utente inviano al suo terminale (computer, tablet, smartphone), dove vengono memorizzati per essere poi ritrasmessi agli stessi siti alla visita successiva. Grazie ai cookie, il sito ricorda le azioni e le preferenze dell&apos;utente (come la lingua, le dimensioni dei caratteri e altre impostazioni di visualizzazione) in modo che non debbano essere indicate nuovamente.
                </p>
              </div>

              <div>
                <h3 className="font-bold text-dark mb-2">Tipologie di cookie utilizzati</h3>

                <div className="space-y-4">
                  <div className="bg-gray-50 rounded-xl p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <span className="inline-block w-2 h-2 rounded-full bg-green-500 flex-shrink-0"></span>
                      <h4 className="font-semibold text-dark text-sm">Cookie Tecnici (sempre attivi)</h4>
                    </div>
                    <p className="text-xs text-gray-600">
                      Sono necessari per il funzionamento del sito e non possono essere disabilitati. Vengono impostati in risposta ad azioni dell&apos;utente come la compilazione di moduli o la memorizzazione delle preferenze di consenso. Non memorizzano informazioni personali identificabili.
                    </p>
                    <div className="mt-3 text-xs text-gray-500 space-y-1">
                      <div className="flex justify-between"><span className="font-medium">cookie_consent</span><span>Memorizza le preferenze cookie dell&apos;utente</span></div>
                      <div className="flex justify-between"><span className="font-medium">session_id</span><span>Gestione della sessione utente</span></div>
                    </div>
                  </div>

                  <div className="bg-gray-50 rounded-xl p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <span className="inline-block w-2 h-2 rounded-full bg-yellow-500 flex-shrink-0"></span>
                      <h4 className="font-semibold text-dark text-sm">Cookie Analitici (previo consenso)</h4>
                    </div>
                    <p className="text-xs text-gray-600">
                      Ci permettono di contare le visite e le sorgenti di traffico per misurare e migliorare le prestazioni del sito. Tutte le informazioni raccolte da questi cookie sono aggregate e quindi anonime.
                    </p>
                    <div className="mt-3 text-xs text-gray-500 space-y-1">
                      <div className="flex justify-between"><span className="font-medium">_ga, _gid</span><span>Google Analytics – analisi del traffico</span></div>
                      <div className="flex justify-between"><span className="font-medium">_gat</span><span>Google Analytics – limitazione delle richieste</span></div>
                    </div>
                  </div>

                  <div className="bg-gray-50 rounded-xl p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <span className="inline-block w-2 h-2 rounded-full bg-red-400 flex-shrink-0"></span>
                      <h4 className="font-semibold text-dark text-sm">Cookie di Marketing (previo consenso)</h4>
                    </div>
                    <p className="text-xs text-gray-600">
                      Possono essere impostati attraverso il nostro sito dai nostri partner pubblicitari. Possono essere utilizzati da queste aziende per costruire un profilo dei tuoi interessi e mostrarti annunci pertinenti su altri siti.
                    </p>
                    <div className="mt-3 text-xs text-gray-500 space-y-1">
                      <div className="flex justify-between"><span className="font-medium">_fbp</span><span>Facebook Pixel – remarketing</span></div>
                      <div className="flex justify-between"><span className="font-medium">li_fat_id</span><span>LinkedIn Insight – conversioni</span></div>
                    </div>
                  </div>
                </div>
              </div>

              <div>
                <h3 className="font-bold text-dark mb-2">Base giuridica del trattamento</h3>
                <p>
                  Il trattamento dei dati tramite cookie tecnici si basa sul legittimo interesse del Titolare (art. 6, par. 1, lett. f) GDPR). Il trattamento tramite cookie analitici e di marketing si basa sul consenso dell&apos;interessato (art. 6, par. 1, lett. a) GDPR), che può essere revocato in qualsiasi momento.
                </p>
              </div>

              <div>
                <h3 className="font-bold text-dark mb-2">Come gestire i cookie</h3>
                <p>
                  L&apos;utente può gestire le proprie preferenze sui cookie in qualsiasi momento tramite il banner che appare alla prima visita del sito. È inoltre possibile configurare il proprio browser per bloccare o eliminare i cookie. Si avverte tuttavia che la disabilitazione dei cookie tecnici potrebbe compromettere il corretto funzionamento del sito.
                </p>
                <p className="mt-2">
                  Per maggiori informazioni su come gestire i cookie nei principali browser:
                </p>
                <ul className="mt-2 space-y-1 pl-4 list-disc text-xs text-gray-600">
                  <li><a href="https://support.google.com/chrome/answer/95647" target="_blank" rel="nofollow noopener noreferrer" className="text-primary hover:underline">Google Chrome</a></li>
                  <li><a href="https://support.mozilla.org/it/kb/Attivare%20e%20disattivare%20i%20cookie" target="_blank" rel="nofollow noopener noreferrer" className="text-primary hover:underline">Mozilla Firefox</a></li>
                  <li><a href="https://support.apple.com/it-it/guide/safari/sfri11471/mac" target="_blank" rel="nofollow noopener noreferrer" className="text-primary hover:underline">Apple Safari</a></li>
                  <li><a href="https://support.microsoft.com/it-it/microsoft-edge/eliminare-i-cookie-in-microsoft-edge-63947406-40ac-c3b8-57b9-2a946a29ae09" target="_blank" rel="nofollow noopener noreferrer" className="text-primary hover:underline">Microsoft Edge</a></li>
                </ul>
              </div>

              <div>
                <h3 className="font-bold text-dark mb-2">Trasferimento dei dati</h3>
                <p>
                  Alcuni cookie di terze parti (es. Google Analytics, Facebook) possono comportare il trasferimento di dati verso paesi extra-UE. Tali trasferimenti avvengono nel rispetto delle garanzie previste dal GDPR (Decisioni di adeguatezza, Clausole Contrattuali Standard).
                </p>
              </div>

              <div>
                <h3 className="font-bold text-dark mb-2">Titolare del trattamento</h3>
                <p>
                  <strong>Big Finance S.r.l.</strong> – Via Antonio Chinotto 1, 00195 Roma (RM) – email:{' '}
                  <a href="mailto:bigfinancesrl@gmail.com" className="text-primary hover:underline">bigfinancesrl@gmail.com</a>
                </p>
              </div>

              <div>
                <h3 className="font-bold text-dark mb-2">Modifiche alla Cookie Policy</h3>
                <p>
                  Il Titolare si riserva il diritto di modificare la presente Cookie Policy in qualsiasi momento. Le modifiche saranno pubblicate su questa pagina con indicazione della data di aggiornamento. Si consiglia di consultare periodicamente questa pagina.
                </p>
              </div>
            </div>

            {/* Footer */}
            <div className="px-8 py-5 border-t border-gray-100 flex-shrink-0">
              <button
                onClick={onClose}
                className="w-full bg-primary text-white py-3 rounded-full font-bold text-sm hover:bg-primary/90 transition-all whitespace-nowrap cursor-pointer"
              >
                Chiudi
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
